import type { ReactElement } from 'react'
import { useState } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import type { FiscalYearEnd } from '~/state/fiscal-year'
import {
  DEFAULT_FISCAL_YEAR_END,
  MONTH_CHOICES,
  dayChoices,
  describeFiscalYearEnd,
  useFiscalYearEnd,
  useSaveFiscalYearEnd,
} from '~/state/fiscal-year'

const CSS = `
.nw-fiscal-year p { margin: 0 0 var(--nw-space-3); }
.nw-fiscal-year .nw-decide { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-4); }
.nw-fiscal-year label > span { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-fiscal-year select { min-width: 150px; }
.nw-fiscal-year .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0; }
.nw-fiscal-year .nw-unsaved { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0; }
.nw-fiscal-year .nw-note { color: var(--nw-muted); font-size: var(--nw-text-chip); max-width: 70ch; }
`

/**
 * The day this household's tax year closes, which is the day a year-end pin
 * reads.
 *
 * Ungated, and beside the base currency rather than among the cards that
 * withhold themselves: every household has a tax year, whether or not it has
 * connected anything, and a household that has to pin a 5 April before the card
 * will appear has nowhere to say so. There is no row to create first.
 */
export function FiscalYearEndPanel(): ReactElement {
  const current = useFiscalYearEnd()
  const save = useSaveFiscalYearEnd()
  const [chosen, setChosen] = useState<FiscalYearEnd | null>(null)

  const stored = current.data?.value
  const shown = chosen ?? stored ?? DEFAULT_FISCAL_YEAR_END
  const edited =
    stored !== undefined && (shown.month !== stored.month || shown.day !== stored.day)
  const busy = current.isPending || save.isPending

  // Moving from 31 January to February would otherwise leave a day the month
  // does not have sitting in the form, and the save would be the first to say so.
  const days = dayChoices(shown.month)
  const day = Math.min(shown.day, days.length)

  return (
    <Card className="nw-fiscal-year">
      <style>{CSS}</style>
      <CardHead
        id="nw-fiscal-year-heading"
        title="The day the tax year closes"
        lede="A year-end pin reads the holdings as they stood on this day, and that reading is what a return is built from. Denmark and most of Europe close on 31 December; the UK closes on 5 April, Australia on 30 June, India on 31 March, and a company closes on whatever date it was incorporated to."
      />

      {current.error && (
        <p className="nw-error">Could not read the year end: {current.error.message}</p>
      )}
      {save.error && <p className="nw-error">Could not save: {save.error.message}</p>}

      <div className="nw-decide">
        <label>
          <span>Closes on</span>
          <select
            value={day}
            disabled={busy}
            aria-label="The day of the month the tax year closes"
            onChange={(event) => setChosen({ month: shown.month, day: Number(event.target.value) })}
          >
            {days.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Month</span>
          <select
            value={shown.month}
            disabled={busy}
            aria-label="The month the tax year closes"
            onChange={(event) => {
              const month = Number(event.target.value)
              setChosen({ month, day: Math.min(day, dayChoices(month).length) })
            }}
          >
            {MONTH_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        {current.data?.stored === false && <StatusPill tone="caution" label="Not chosen yet" />}
        <Button
          kind="primary"
          disabled={busy || !edited}
          onClick={() =>
            save.mutate({ month: shown.month, day }, { onSuccess: () => setChosen(null) })
          }
        >
          {save.isPending ? 'Saving…' : 'Save year end'}
        </Button>
        {edited && !save.isPending && (
          <p className="nw-unsaved">Not saved yet — nothing pinned has moved.</p>
        )}
      </div>

      {/*
        The half of this change a household would otherwise discover by reading a
        CSV in front of an accountant: moving the date does not restate what is
        already on file.
      */}
      <p className="nw-note">
        Year ends already pinned keep the day they were read on. Changing this decides which day the
        next pin reads — {describeFiscalYearEnd({ month: shown.month, day })} — and leaves every
        record already filed saying what it said.
      </p>
    </Card>
  )
}
